import { useState } from 'react'

import { useQuery } from '@tanstack/react-query'
import { isAfter, isBefore, isEqual, isWeekend } from 'date-fns'

import { cn } from '@fortem-monorepo/js-utilities'
import { getTMinus, getTMinus1, getTMinus2, toISO8601 } from '@fortem-monorepo/js-utilities'
import { PageSidebar, useApi } from '@fortem-monorepo/react-spa'
import { ViewContainer } from '@fortem-monorepo/react-spa'
import { useConfirmModal } from '@fortem-monorepo/react-spa'
import { useUserContext } from '@fortem-monorepo/react-spa'
import { DatePicker } from '@fortem-monorepo/react-ui'
import { LoadingButton } from '@fortem-monorepo/react-ui'
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '@fortem-monorepo/react-ui'
import {
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarSeparator,
} from '@fortem-monorepo/react-ui'
import { MonthlyRoll } from '@fortem-monorepo/web-sdk'

import { Page } from '@/components/Page'
import { OPERATIONS } from '@/constants/links'
import { MonthlyRollAdditionalTradesTable } from '@/pages/operations/monthly_roll/components/MonthlyRollAdditionalTradesTable'
import { MonthlyRollDataTable } from '@/pages/operations/monthly_roll/components/MonthlyRollDataTable'
import { MonthlyRollPendingTradesTable } from '@/pages/operations/monthly_roll/components/MonthlyRollPendingTradesTable'
import { MonthlyRollPerfTP1Table } from '@/pages/operations/monthly_roll/components/MonthlyRollPerfTP1Table'
import { MonthlyRollPerfTable } from '@/pages/operations/monthly_roll/components/MonthlyRollPerfTable'
import { useSendMonthlyRollEmailMutation } from '@/react-query/mutations/monthly-roll'
import { getMonthlyRollOptions } from '@/react-query/queries/monthly-roll'

export interface MonthlyRollPageProps {}

export const MonthlyRollPage: React.FC<MonthlyRollPageProps> = () => {
  const api = useApi()
  const [value_date, set_value_date] = useState(getTMinus2())
  const [value_date_tmo, set_value_date_tmo] = useState<Date | undefined>(getTMinus(3))
  const [value_date_tpo, set_value_date_tpo] = useState<Date | undefined>(getTMinus1())
  const [spot_date, set_spot_date] = useState<Date | undefined>(undefined)
  const [fwd_date, set_fwd_date] = useState<Date | undefined>(undefined)

  const params = {
    value_date: value_date ? toISO8601(value_date) : undefined,
    value_date_tmo: value_date_tmo ? toISO8601(value_date_tmo) : undefined,
    value_date_tpo: value_date_tpo ? toISO8601(value_date_tpo) : undefined,
    spot_date: spot_date ? toISO8601(spot_date) : undefined,
    fwd_date: fwd_date ? toISO8601(fwd_date) : undefined,
  }

  // TODO: Will Sambells - fix this params issue, casting as type is wrong and not safe
  const query = useQuery({
    ...getMonthlyRollOptions(api, params as MonthlyRoll.GetMonthlyRollParams),
    enabled: false,
  })

  const hasPendingTrades = (query.data?.pending_trades ?? []).length > 0

  return (
    <Page metadata={{ title: 'Monthly Roll' }} permissions={OPERATIONS.MONTHLY_ROLL.permissions}>
      <div className={'flex flex-1'}>
        <MonthlyRollSidebar
          // @ts-ignore
          params={params}
          value_date={value_date}
          set_value_date={set_value_date}
          value_date_tmo={value_date_tmo}
          set_value_date_tmo={set_value_date_tmo}
          value_date_tpo={value_date_tpo}
          set_value_date_tpo={set_value_date_tpo}
          spot_date={spot_date}
          set_spot_date={set_spot_date}
          fwd_date={fwd_date}
          set_fwd_date={set_fwd_date}
        />
        <div className="flex flex-col flex-1">
          {!!query.data || query.isFetching ? (
            <ResizablePanelGroup direction={'vertical'} className={'flex-1'}>
              <ResizablePanel order={1}>
                <ResizablePanelGroup direction={'horizontal'} autoSaveId={'conditional'}>
                  <ResizablePanel
                    minSize={10}
                    id="panel-data"
                    order={1}
                    defaultSize={hasPendingTrades ? 50 : 100}
                  >
                    <MonthlyRollDataTable params={params as MonthlyRoll.GetMonthlyRollParams} />
                  </ResizablePanel>
                  {hasPendingTrades && (
                    <>
                      <ResizableHandle />
                      <ResizablePanel
                        minSize={10}
                        id="panel-pending-trades"
                        order={2}
                        defaultSize={50}
                      >
                        <MonthlyRollPendingTradesTable
                          params={params as MonthlyRoll.GetMonthlyRollParams}
                        />
                      </ResizablePanel>
                    </>
                  )}
                </ResizablePanelGroup>
              </ResizablePanel>
              <ResizableHandle />
              <ResizablePanel minSize={10} order={2} className={'w-full'}>
                <ResizablePanelGroup direction={'horizontal'}>
                  <ResizablePanel defaultSize={50} minSize={5} order={1}>
                    <MonthlyRollAdditionalTradesTable
                      params={params as MonthlyRoll.GetMonthlyRollParams}
                    />
                  </ResizablePanel>
                  <ResizableHandle />
                  <ResizablePanel minSize={5} order={2} defaultSize={25}>
                    <MonthlyRollPerfTable params={params as MonthlyRoll.GetMonthlyRollParams} />
                  </ResizablePanel>
                  <ResizableHandle />
                  <ResizablePanel minSize={5} order={3} defaultSize={25}>
                    <MonthlyRollPerfTP1Table params={params as MonthlyRoll.GetMonthlyRollParams} />
                  </ResizablePanel>
                </ResizablePanelGroup>
              </ResizablePanel>
            </ResizablePanelGroup>
          ) : (
            <ViewContainer>
              <span className={'text-muted-foreground'}>Select dates to get started.</span>
            </ViewContainer>
          )}
        </div>
      </div>
    </Page>
  )
}

interface MonthlyRollSidebarProps {
  params: MonthlyRoll.GetMonthlyRollParams
  //
  value_date: Date
  set_value_date: (date: Date) => void
  //
  value_date_tmo?: Date
  set_value_date_tmo: (date: Date) => void
  //
  value_date_tpo?: Date
  set_value_date_tpo: (date: Date) => void
  //
  spot_date?: Date
  set_spot_date: (date: Date) => void
  //
  fwd_date?: Date
  set_fwd_date: (date: Date) => void
}

const MonthlyRollSidebar: React.FC<MonthlyRollSidebarProps> = (props) => {
  const api = useApi()
  const { value_date, value_date_tmo, value_date_tpo, spot_date, fwd_date, params } = props

  const query = useQuery({
    ...getMonthlyRollOptions(api, params),
    enabled: false,
  })

  const sendEmailMutation = useSendMonthlyRollEmailMutation()

  const { user } = useUserContext()

  const { Dialog: RunReportDialog, withConfirmation } = useConfirmModal({
    title: `Send Monthly Roll Email`,
    description: `Send Monthy Roll to ${user.user?.email} and relevant recipients. Continue?`,
  })

  const hasValidParams = [
    !!value_date,
    !!value_date_tmo,
    !!value_date_tpo,
    !!spot_date,
    !!fwd_date,
  ].every((val) => !!val)

  const canFetchData = hasValidParams && !query.isFetching
  const inputsDisabled = query.isFetching || sendEmailMutation.isPending

  return (
    <PageSidebar
      style={
        {
          '--sidebar-width': '220px',
        } as React.CSSProperties
      }
    >
      <SidebarContent className={'min-h-auto!'}>
        <SidebarGroup className={'pb-0'}>
          <SidebarGroupLabel className={'h-auto! mb-2'}>Date</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuButton asChild>
                <DatePicker
                  buttonDisabled={inputsDisabled}
                  selected={props.value_date}
                  onSelect={(date) => props.set_value_date(date!)}
                  disabled={(date) => isWeekend(date)}
                />
              </SidebarMenuButton>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
        <SidebarGroup className={'pb-0'}>
          <SidebarGroupLabel className={'h-auto! mb-2'}>Date T-1</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuButton asChild>
                <DatePicker
                  buttonDisabled={inputsDisabled}
                  selected={props.value_date_tmo}
                  onSelect={(date) => props.set_value_date_tmo(date!)}
                  disabled={(date) =>
                    isAfter(date, props.value_date) ||
                    isEqual(date, props.value_date) ||
                    isWeekend(date)
                  }
                />
              </SidebarMenuButton>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
        <SidebarGroup className={'pb-0'}>
          <SidebarGroupLabel className={'h-auto! mb-2'}>Date T+1</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuButton asChild>
                <DatePicker
                  buttonDisabled={inputsDisabled}
                  selected={props.value_date_tpo}
                  onSelect={(date) => props.set_value_date_tpo(date!)}
                  disabled={(date) =>
                    isBefore(date, props.value_date) ||
                    isEqual(date, props.value_date) ||
                    isWeekend(date)
                  }
                />
              </SidebarMenuButton>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
        <SidebarGroup className={'pb-0'}>
          <SidebarGroupLabel className={'h-auto! mb-2'}>Spot Date</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuButton asChild>
                <DatePicker
                  buttonDisabled={inputsDisabled}
                  selected={props.spot_date}
                  onSelect={(date) => props.set_spot_date(date!)}
                  disabled={(date) =>
                    isBefore(date, props.value_date) || isEqual(date, props.value_date)
                  }
                />
              </SidebarMenuButton>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
        <SidebarGroup>
          <SidebarGroupLabel className={'h-auto! mb-2'}>FWD Date</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuButton asChild>
                <DatePicker
                  buttonDisabled={inputsDisabled}
                  selected={props.fwd_date}
                  onSelect={(date) => props.set_fwd_date(date!)}
                  disabled={(date) =>
                    isBefore(date, props.value_date) || isEqual(date, props.value_date)
                  }
                />
              </SidebarMenuButton>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
        <SidebarSeparator />
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuButton
                asChild
                disabled={!hasValidParams || (!canFetchData && !!query.data)}
                className={cn('transition-all', {
                  ['border-fortem-orange/50 border-2']:
                    hasValidParams && !query.data && !query.isFetching,
                })}
              >
                <LoadingButton variant={'outline'} onClick={async () => query.refetch()}>
                  Run Report
                </LoadingButton>
              </SidebarMenuButton>
              <SidebarMenuButton asChild disabled={!canFetchData || !query.data}>
                <LoadingButton
                  variant={'outline'}
                  onClick={async () =>
                    withConfirmation(() => sendEmailMutation.mutateAsync(params))
                  }
                >
                  Send Email
                </LoadingButton>
              </SidebarMenuButton>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <RunReportDialog />
    </PageSidebar>
  )
}
